import * as bus from "@sinch/bus";
import { atom, useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import {
  isSomeCookieConsentGranted,
  useIsConsentRequired,
} from "../components/CookieBanner/consent";
import { onesaasConsentAtom } from "../components/CookieBanner/onesaas-consent-cookie";
import { useHashedValue } from "../hooks/useHashedValue";
import { getTrackEventPayloadHandler } from "./handleTrackEventPayload";
import { rudderstackReadyAtom } from "./rudderstack";
import { formatEasyClickEvent } from "./shellEventHandler";
import { shellListener } from "./shellEventListener";
import { DataCollectors } from "./types";
import type { AnalyticsUser, TrackEventPayload } from "./types";
import { useInitRudderstackSession } from "./useInitRudderstackSession";

export type EventListenerStatus = "unset" | "anonymous" | "user";

const storedEventsAtom = atom<bus.TRACK_EVENT_PAYLOAD[]>([]);
const storedEasyClicksEventsAtom = atom<TrackEventPayload[]>([]);

export const useSetEventListener = (
  user: AnalyticsUser | null,
  isEnabled = true,
  dataCollectors: DataCollectors[] = [
    DataCollectors.RudderStack,
    DataCollectors.Datadog,
  ]
) => {
  const [isListenerSet, setIsListenerSet] = useState<boolean>(true);
  const onesaasConsent = useAtomValue(onesaasConsentAtom);
  const shouldHandleConsent = useIsConsentRequired();
  const hashedEmail = useHashedValue(user?.email);
  const rsReadyAtom = useAtomValue(rudderstackReadyAtom);

  const [storedEvents, storeEvents] = useAtom(storedEventsAtom);
  const [storedEasyClickEvents, storeEasyClickEvents] = useAtom(
    storedEasyClicksEventsAtom
  );

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const userIsNullOrHasEmailButNotHashedYet =
      !user || (user?.email && !hashedEmail);

    if (userIsNullOrHasEmailButNotHashedYet || !rsReadyAtom) {
      const terminateBusListener = bus.listen(async (message) => {
        if (bus.isData(message)) {
          storeEvents((events) => [...events, message.payload]);
        }
      }, bus.isTrackEvent);

      const terminateShellListener = shellListener((event) => {
        const trackEventPayload = formatEasyClickEvent(event);
        if (trackEventPayload) {
          storeEasyClickEvents((events) => [...events, trackEventPayload]);
        }
      });

      return () => {
        terminateBusListener();
        terminateShellListener();
      };
    }

    const userWithHashedEmail = user
      ? { ...user, settings: { ...user.settings, hashedEmail } }
      : null;

    const busTrackEventstHandler = getTrackEventPayloadHandler(
      dataCollectors,
      { user: userWithHashedEmail ?? {} },
      shouldHandleConsent,
      onesaasConsent
    );

    const terminateBusListener = bus.listen(async (message) => {
      if (bus.isData(message)) {
        busTrackEventstHandler(message.payload);
      }
    }, bus.isTrackEvent);

    const terminateShellListener = shellListener((event) => {
      const trackEventPayload = formatEasyClickEvent(event);
      if (trackEventPayload) {
        busTrackEventstHandler(trackEventPayload);
      }
    });
    setIsListenerSet(true);

    if (storedEvents.length) {
      storedEvents.forEach(busTrackEventstHandler);
      storeEvents([]);
    }

    if (storedEasyClickEvents.length) {
      storedEasyClickEvents.forEach(busTrackEventstHandler);
      storeEasyClickEvents([]);
    }

    return () => {
      terminateBusListener();
      terminateShellListener();
    };
  }, [
    user,
    onesaasConsent,
    shouldHandleConsent,
    hashedEmail,
    storedEvents,
    storedEasyClickEvents,
    rsReadyAtom,
    storeEvents,
    storeEasyClickEvents,
    isEnabled,
    dataCollectors,
  ]);

  return isListenerSet;
};

export const useSetUserlessEventListener = (
  dataCollectors: DataCollectors[] = [
    DataCollectors.RudderStack,
    DataCollectors.Datadog,
  ]
) => {
  const [isListenerSet, setIsListenerSet] = useState(false);
  const onesaasConsent = useAtomValue(onesaasConsentAtom);
  const shouldHandleConsent = useIsConsentRequired();
  const rsReadyAtom = useAtomValue(rudderstackReadyAtom);
  const [storedEvents, storeEvents] = useAtom(storedEventsAtom);
  const [storedEasyClickEvents, storeEasyClickEvents] = useAtom(
    storedEasyClicksEventsAtom
  );

  useInitRudderstackSession(
    !shouldHandleConsent || isSomeCookieConsentGranted(onesaasConsent)
  );

  useEffect(() => {
    if (!rsReadyAtom) {
      const terminateBusListener = bus.listen(async (message) => {
        if (bus.isData(message)) {
          storeEvents((events) => [...events, message.payload]);
        }
      }, bus.isTrackEvent);

      const terminateShellListener = shellListener((event) => {
        const trackEventPayload = formatEasyClickEvent(event);
        if (trackEventPayload) {
          storeEasyClickEvents((events) => [...events, trackEventPayload]);
        }
      });

      return () => {
        terminateBusListener();
        terminateShellListener();
      };
    }

    const busTrackEventstHandler = getTrackEventPayloadHandler(
      dataCollectors,
      {
        user: {},
      },
      shouldHandleConsent,
      onesaasConsent
    );

    if (storedEvents.length) {
      storedEvents.forEach(busTrackEventstHandler);
      storeEvents([]);
    }

    if (storedEasyClickEvents.length) {
      storedEasyClickEvents.forEach(busTrackEventstHandler);
      storeEasyClickEvents([]);
    }

    const terminateBusListener = bus.listen(async (message) => {
      if (bus.isData(message)) {
        busTrackEventstHandler(message.payload);
      }
    }, bus.isTrackEvent);
    const terminateShellListener = shellListener((event) => {
      const trackEventPayload = formatEasyClickEvent(event);
      if (trackEventPayload) {
        busTrackEventstHandler(trackEventPayload);
      }
    });
    setIsListenerSet(true);
    return () => {
      terminateBusListener();
      terminateShellListener();
    };
  }, [
    onesaasConsent,
    shouldHandleConsent,
    rsReadyAtom,
    storedEvents,
    storedEasyClickEvents,
    storeEvents,
    storeEasyClickEvents,
    dataCollectors,
  ]);

  return isListenerSet;
};
